import { useEffect, useState } from "react";
import { base44 } from "@/api/base44Client";
import AdminGuard from "@/components/admin/AdminGuard";
import AdminLayout from "@/components/admin/AdminLayout";
import { useApp } from "@/lib/AppContext";

const EMPTY = { title: "", title_en: "", image_url: "", link_url: "", is_active: true, sort_order: 0 };

const T = {
  ar: {
    title: "إدارة البانرات الترويجية",
    addNew: "إضافة بانر جديد",
    save: "حفظ", cancel: "إلغاء", delete: "حذف",
    titleAr: "العنوان (عربي) *", titleEn: "العنوان (إنجليزي)",
    imageUrl: "رابط الصورة *", linkUrl: "رابط الضغط (اختياري)",
    order: "الترتيب", active: "نشط",
    empty: "لا توجد بانرات — أضف أول بانر",
    saving: "⏳ جاري الحفظ..."
  },
  en: {
    title: "Manage Promo Banners",
    addNew: "Add New Banner",
    save: "Save", cancel: "Cancel", delete: "Delete",
    titleAr: "Title (Arabic) *", titleEn: "Title (English)",
    imageUrl: "Image URL *", linkUrl: "Click Link (optional)",
    order: "Order", active: "Active",
    empty: "No banners yet — add your first banner",
    saving: "⏳ Saving..."
  }
};

export default function AdminPromos() {
  const { lang } = useApp();
  const tr = T[lang] || T.ar;
  const isAr = lang === "ar";
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    base44.entities.PromoImage.list("sort_order", 20)
      .then(data => { setPromos(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image_url) return;
    setSaving(true);
    if (editing) {
      const updated = await base44.entities.PromoImage.update(editing.id, form);
      setPromos(prev => prev.map(p => p.id === editing.id ? updated : p));
    } else {
      const created = await base44.entities.PromoImage.create(form);
      setPromos(prev => [...prev, created]);
    }
    setSaving(false);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isAr ? "هل أنت متأكد؟" : "Are you sure?")) return;
    await base44.entities.PromoImage.delete(id);
    setPromos(prev => prev.filter(p => p.id !== id));
  };

  const toggleActive = async (p) => {
    const updated = await base44.entities.PromoImage.update(p.id, { is_active: !p.is_active });
    setPromos(prev => prev.map(x => x.id === p.id ? updated : x));
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-5" dir={isAr ? "rtl" : "ltr"}>
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-black text-foreground">{tr.title}</h1>
            <button onClick={openNew}
              className="bg-primary text-primary-foreground font-bold px-4 py-2.5 rounded-xl text-sm hover:opacity-90 transition-all">
              + {tr.addNew}
            </button>
          </div>

          {/* Form */}
          {showForm && (
            <form onSubmit={handleSave} className="bg-card border border-border rounded-2xl p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.titleAr}</label>
                  <input value={form.title} onChange={e => upd("title", e.target.value)}
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.titleEn}</label>
                  <input value={form.title_en} onChange={e => upd("title_en", e.target.value)}
                    dir="ltr"
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.imageUrl}</label>
                <input value={form.image_url} onChange={e => upd("image_url", e.target.value)}
                  dir="ltr"
                  className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                {form.image_url && (
                  <img src={form.image_url} alt="preview" className="mt-2 rounded-xl max-h-32 object-cover" />
                )}
              </div>
              <div>
                <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.linkUrl}</label>
                <input value={form.link_url} onChange={e => upd("link_url", e.target.value)}
                  dir="ltr"
                  className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.order}</label>
                  <input type="number" value={form.sort_order} onChange={e => upd("sort_order", Number(e.target.value))}
                    className="w-20 bg-secondary border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-primary text-foreground text-center" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-4">
                  <input type="checkbox" checked={form.is_active} onChange={e => upd("is_active", e.target.checked)}
                    className="w-4 h-4 accent-primary" />
                  <span className="text-sm font-bold text-foreground">{tr.active}</span>
                </label>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={saving}
                  className="bg-primary text-primary-foreground font-bold px-5 py-2.5 rounded-xl text-sm hover:opacity-90 disabled:opacity-60 transition-all">
                  {saving ? tr.saving : tr.save}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="bg-secondary text-muted-foreground font-bold px-5 py-2.5 rounded-xl text-sm hover:opacity-80 transition-all">
                  {tr.cancel}
                </button>
              </div>
            </form>
          )}

          {/* List */}
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => <div key={i} className="bg-card rounded-2xl p-5 animate-pulse border border-border h-28" />)}
            </div>
          ) : promos.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">{tr.empty}</div>
          ) : (
            <div className="space-y-3">
              {promos.map(p => (
                <div key={p.id} className={`bg-card border rounded-2xl p-4 flex flex-wrap items-center gap-4 ${p.is_active ? "border-border" : "border-border opacity-60"}`}>
                  <img src={p.image_url} alt={p.title} className="w-20 h-14 object-cover rounded-xl flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-foreground text-sm">{p.title}</p>
                    {p.title_en && <p className="text-xs text-muted-foreground">{p.title_en}</p>}
                    {p.link_url && <p className="text-xs text-primary truncate">{p.link_url}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => toggleActive(p)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-xl transition-all ${p.is_active ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "bg-secondary text-muted-foreground"}`}>
                      {p.is_active ? (isAr ? "نشط" : "Active") : (isAr ? "غير نشط" : "Inactive")}
                    </button>
                    <button onClick={() => openEdit(p)}
                      className="bg-secondary text-muted-foreground text-xs font-bold px-3 py-1.5 rounded-xl hover:opacity-80 transition-all">
                      ✏️
                    </button>
                    <button onClick={() => handleDelete(p.id)}
                      className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-bold px-3 py-1.5 rounded-xl hover:opacity-80 transition-all">
                      {tr.delete}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}